import React from 'react';
import { connect } from 'react-redux';
import { formActions, mergeActionsToProps } from 'redux-pure-form';

import EventContainer from './calendar.event-container.js';
import {saveCounter, checkOut} from '../calendar-day.actions.js';
import Datepicker from "../../common/datepicker/datepicker";
import {setModal} from '../../ui/ui.actions.js';
import {EVENT_CHECKOUT, MAINTENANCE_EVENT_TYPE} from '../calendar.consts.js';
import Timepicker from "../../common/timepicker/timepicker";
import DurationPicker from "../../common/durationpicker/durationpicker";
import {dateToTime, unixToTime, timeToUnix} from "../../../utils/date";
import {isObjectEmpty} from "../../../utils/object";
import {newDate} from '../calendar.model.js';

import Inspections from './calendar.event-inspections';

class EventCheckout extends React.Component {
  static footer = true;

  state = {
    departureTime: null,
    flightTime: null,
    expectedArrivalTime: null
  };

  constructor(props) {
    super(props);
    this.handleCounterEditOpen = this.handleCounterToggle.bind(this, true);
    this.handleCounterEditClose = this.handleCounterToggle.bind(this, false);
    this.handleCounterEdit = this.handleCounterEdit.bind(this);
    const {organization, event, timezone} = this.props;
    const enableTrackFlight = organization.organizationSettings.enableTrackFlight === "1";
    const timingReady = enableTrackFlight && !isObjectEmpty(event) && !isObjectEmpty(timezone);

    this.state = {
      ...(timingReady ? this.getEventTiming(event) : {}),
      enableTrackFlight
    };
  }

  getEventTiming = (event) => {
    const {user, timezone} = this.props;
    let flightTime;

    if (event) {
      const startTime = parseInt(event.startTime);
      const finishTime = parseInt(event.finishTime);

      const flightTimeUnix = !isNaN(startTime) && !isNaN(finishTime)
        ? finishTime - startTime
        : user.eventDefaultLength;

      flightTime = unixToTime(flightTimeUnix * 1000);
    } else {
      flightTime = unixToTime((user.eventDefaultLength) * 1000);
    }

    const eventTimezone = event && event.timeZone ? event.timeZone[event.timeZoneID] : {offset: 0};
    const departureTime = newDate(timezone.timeNow, eventTimezone.offset);
    const expectedArrivalTime = new Date(departureTime.getTime() + timeToUnix(flightTime));

    return {
      departureTime,
      flightTime,
      expectedArrivalTime
    };
  };

  componentDidUpdate = prevProps => {
    const {event, timezone} = this.props;
    const {enableTrackFlight} = this.state;
    const timingReady =
      enableTrackFlight &&
      isObjectEmpty(prevProps.event) &&
      !isObjectEmpty(event) &&
      !isObjectEmpty(timezone);

    if (timingReady) {
      const eventTiming = this.getEventTiming(event);
      this.setState({...eventTiming});
    }
  };

  handleCounterEdit = async (e) => {
    e.preventDefault();
    const { saveCounter, setModal: updateCounterState } = this.props;
    const { id } = e.currentTarget.dataset;

    await saveCounter(id);
    updateCounterState(false, `counter-${id}`);
  };

  handleCounterToggle(state, e) {
    const { id } = e.currentTarget.dataset;
    return this.props.setModal(state, `counter-${id}`);
  }

  onCheckOut = () => {
    const {enableTrackFlight} = this.state;
    const {event} = this.props;
    let params;

    if (enableTrackFlight) {
      const {flightTime, departureTime, expectedArrivalTime} = this.state;
      const decimalFlightTime = timeToUnix(flightTime) / 60000 / 60;

      params = {
        flightTime: decimalFlightTime.toFixed(10),
        departureTime: departureTime.toString(),
        expectedArrivalTime: expectedArrivalTime.toString(),
        timeZoneId: event.timeZoneID
      };
    }
    this.props.checkOut(params);
  };

  onUpdateDepartureDate = date => {
    const {departureTime} = this.state;
    const newDate = date;
    const oldHours = departureTime.getHours();
    const oldMinutes = departureTime.getMinutes();

    newDate.setHours(oldHours, oldMinutes);

    this.setState({departureTime: newDate});
  };

  onUpdateDepartureTime = time => {
    const {departureTime} = this.state;
    const [hours, minutes] = time.split(":");

    departureTime.setHours(hours, minutes);

    this.setState({departureTime}, () => {
      const {flightTime} = this.state;

      this.onTimeChangeWithDep("flightTime", flightTime);
    });
  };

  onTimeChangeWithDep = (rootValue, timeValue) => {
    const {departureTime} = this.state;

    if (rootValue === "flightTime") {
      const unixFlightTime = timeToUnix(timeValue);
      const departureTimeUnix = departureTime.getTime();
      const expectedUnixTime = departureTimeUnix + unixFlightTime;
      const expectedArrivalTime = new Date(expectedUnixTime);

      this.setState({
        flightTime: timeValue,
        expectedArrivalTime: expectedArrivalTime
      });
    } else if (rootValue === "expectedArrivalTime") {
      const [hours, minutes] = timeValue.split(":");
      const unixDepartureTime = departureTime.getTime();
      const newExArrivalTime = new Date(unixDepartureTime);

      newExArrivalTime.setHours(hours, minutes);

      let flightTime = newExArrivalTime.getTime() - unixDepartureTime;

      if (flightTime < 0) {
        newExArrivalTime.setDate(newExArrivalTime.getDate() + 1);
        flightTime = newExArrivalTime.getTime() - unixDepartureTime;
      }

      this.setState({
        flightTime: unixToTime(flightTime),
        expectedArrivalTime: newExArrivalTime
      });
    }
  };

  render() {
    const {UI, accessgroups = {}, checkoutForm, fieldAttrs, user, event} = this.props;
    const {expectedArrivalTime, flightTime, departureTime, enableTrackFlight} = this.state;

    if (!Object.keys(checkoutForm).length) return null;

    const userIsAPilot = event.pilotID === user.id;
    const meterView = userIsAPilot || accessgroups.meterView;
    const counterEdit = userIsAPilot || accessgroups.counterEdit;
    const shouldShowMeters = meterView
      && checkoutForm.aircraft
      && (checkoutForm.aircraft.aircraftID || checkoutForm.aircraft.simulatorID);

    const isMaintenanceEvent = event.type === MAINTENANCE_EVENT_TYPE;

    return (
      <EventContainer>
        <div className="checkbox form-inline">
          {enableTrackFlight && !isMaintenanceEvent && (
            <div className="row track-flight-form">
              <div className="form-group col-xs-12">
                <label htmlFor="departureTime" className="time-label col-sm-3 col-xs-4">
                  Departure Time
                </label>
                <div className="col-sm-9 col-xs-8 padding-left-0">
                  <div className="row">
                    <div className="col-sm-4 col-xs-6">
                      <Datepicker
                        id="period"
                        dateFormat={user.settings.date}
                        selected={departureTime}
                        className="form-control inner-datepicker input-sm"
                        onChange={this.onUpdateDepartureDate}
                        required
                      />
                    </div>
                    <div className="col-xs-2 padding-left-0">
                      <Timepicker
                        value={dateToTime(departureTime)}
                        onChange={this.onUpdateDepartureTime}
                        required
                        id="departure-time"
                        name="departure-time"
                      />
                    </div>
                  </div>
                </div>
              </div>
              <div className="form-group col-xs-12">
                <label htmlFor="flightTime" className="time-label col-sm-3">
                  Flight Time
                </label>
                <DurationPicker
                  value={flightTime}
                  onChange={time => this.onTimeChangeWithDep("flightTime", time)}
                  required={true}
                  id={"flight-time"}
                  name={"flight-time"}
                />
              </div>
              <div className="form-group col-xs-12">
                <label htmlFor="expectedArrivalTime" className="time-label col-sm-3">
                  Expected Arrival Time
                </label>
                <Timepicker
                  value={dateToTime(expectedArrivalTime)}
                  onChange={time => this.onTimeChangeWithDep("expectedArrivalTime", time)}
                  required={true}
                  id="arrival-time"
                  name="arrival-time"
                />
              </div>
            </div>
          )}
          {shouldShowMeters && (
            <table className="table table-md meters-table">
              <thead>
                <tr>
                  <td width="120px">
                    <span className="text-muted">Meters</span>
                  </td>
                  <td>
                    <span className="text-muted">Start</span>
                  </td>
                </tr>
              </thead>
              <tbody>
                {checkoutForm.countersList.map(counterID => {
                  const counter = checkoutForm.counters[counterID];

                  let prevEventCounterValue = counter.value;

                  if (event.previousCheckedInEventCounters !== null) {
                    const prevCounter = event.previousCheckedInEventCounters.find(counter => counter.id === counterID);

                    if (prevCounter && prevCounter.value) {
                      prevEventCounterValue = prevCounter.value;
                    }
                  }

                  return (
                    <tr key={counterID}>
                      <td>
                        <div className="compact">
                          <b children={counter.name}/>
                        </div>
                      </td>
                      <td className="text-left">
                        {UI.modals[`counter-${counterID}`] && counterEdit ? (
                          <form
                            className="form-inline handle-counter-edit-form"
                            onSubmit={this.handleCounterEdit}
                            data-id={counterID}
                          >
                            <input
                              type="number"
                              maxLength="10"
                              className="form-control"
                              placeholder="Value"
                              value={checkoutForm.counters[counterID].value || ''}
                              name={`checkoutForm.counters.${counterID}.value`}
                              {...fieldAttrs}
                            />
                            <input
                              type="text"
                              className="form-control"
                              placeholder="Comment"
                              value={checkoutForm.counters[counterID].comments || ''}
                              name={`checkoutForm.counters.${counterID}.comments`}
                              {...fieldAttrs}
                            />
                            <button
                              type="submit"
                              disabled={UI.progress}
                              data-id={counterID}
                              className="btn btn-success btn-sm"
                            >
                              Save
                            </button>
                            <button
                              type="button"
                              disabled={UI.progress}
                              onClick={this.handleCounterEditClose}
                              data-id={counterID}
                              className="btn btn-default btn-sm"
                            >
                              <i className="icon cancel-circled" />
                            </button>
                          </form>
                        ) : (
                          <span>
                            {prevEventCounterValue}{' '}
                            {counterEdit && (
                              <span
                                className="link--ajax"
                                onClick={this.handleCounterEditOpen}
                                data-id={counterID}
                                children={"Edit"}
                              />
                            )}
                          </span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}

          <Inspections tableStyle="checkout"/>
        </div>
        <input id={EVENT_CHECKOUT} onClick={this.onCheckOut} style={{ display: 'none' }} />
      </EventContainer>
    );
  }
}

const mapStateToProps = state => ({
  UI: state.UI,
  user: state.user,
  event: state.event,
  timezone: state.timezone,
  organization: state.organization,
  accessgroups: state.accessgroups,
  checkoutForm: state.checkoutForm
});

export default connect(
  mapStateToProps,
  {
    setModal,
    saveCounter,
    checkOut,
    ...formActions
  },
  mergeActionsToProps
)(EventCheckout);
